import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { useGetNextNumberingOfWorkGroup } from "../../Hooks/useGetNextNumberingOfWorkGroup";
import { CREATE_WORK_GROUP, UPDATE_WORK_GROUP } from "../../Schema/WorkGroup";
import ProvincailCouncilList from "./ProvincailCouncilList/ProvincailCouncilList";
import SecretaryList from "./SecretaryList/SecretaryList";


export default function WorkGroupForm({
  open,
  editData,
  refetchWorkGroupById,
  setRefetch,
  handleClose,
  dialogTitle,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createWorkGroup] = useMutation(CREATE_WORK_GROUP, {
    onCompleted: ({ createWorkGroup }) => {
      setLoading(false);
      if (createWorkGroup?.isSuccess === true) {
        setAlert(true, "success", createWorkGroup?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createWorkGroup?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateWorkGroup] = useMutation(UPDATE_WORK_GROUP, {
    onCompleted: ({ updateWorkGroup }) => {
      setLoading(false);
      if (updateWorkGroup?.isSuccess === true) {
        setAlert(true, "success", updateWorkGroup?.message);
        handleClose();
        resetForm();
        setRefetch();
        refetchWorkGroupById();
      } else {
        setAlert(true, "error", updateWorkGroup?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });


  const newProvincailCouncil = {
    provincialCouncilInMandateId: {id: "", title: ""},
    roleInWorkGroup: 'member'
  }
  const [provincailCouncilList, setProvincailCouncilList] = useState([newProvincailCouncil])

  const newSecretary = {
    secretaryInMandateId: {id: "", title: ""},
    roleInWorkGroup: 'member'
  }

  const [secretaryList, setSecretaryList] = useState([newSecretary])

  
  //fromik
  const CheckValidation = Yup.object().shape({
    numbering: Yup.number().nullable().required(t("required")),
    work_group_name: Yup.string().required(t("required")),
    responsibilities: Yup.string(),
   
  });

  const formik = useFormik({
    initialValues: {
        numbering: null,
        work_group_name: "",
        responsibilities: "",
    },

    validationSchema: CheckValidation,

    onSubmit: async (values) => {

      setLoading(true);
      const inputData = {
        numbering: parseFloat(values?.numbering),
        work_group_name: values?.work_group_name,
        responsibilities: values?.responsibilities,
        provincial_council: provincailCouncilList.map(e => {
          return {
            provincialCouncilInMandateId: e?.provincialCouncilInMandateId?.id,
            roleInWorkGroup: e?.roleInWorkGroup
          }
        }),
        secretary: secretaryList.map(e => {
          return {
            secretaryInMandateId: e?.secretaryInMandateId?.id,
            roleInWorkGroup: e?.roleInWorkGroup
          }
        }),
      }
    
      if (dialogTitle === "Update") {

        updateWorkGroup({
          variables: {
            id: editData?._id,
            input: inputData,
          },
        });
      } else {
        
        createWorkGroup({
          variables: {
            input: inputData,
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  const {numbering, refetch } = useGetNextNumberingOfWorkGroup({setFieldValue});
 
  useEffect(() => {
    refetch();
    if (dialogTitle === "Create") {
      setFieldValue("numbering", numbering);
    }
  }, [dialogTitle, open]);

  useEffect(() => {
    if (editData) {
      setFieldValue("numbering", editData?.numbering);
      setFieldValue("work_group_name", editData?.work_group_name);
      setFieldValue("responsibilities", editData?.responsibilities);
      setProvincailCouncilList(editData?.provincial_council?.map((e => {
        return{
          provincialCouncilInMandateId: {id: e?.provincialCouncilInMandateId, title: e?.lastNameKh + " " + e?.firstNameKh},
          roleInWorkGroup: e?.roleInWorkGroup
        }
      })))
      setSecretaryList(editData?.secretary?.map((e => {
        return{
          secretaryInMandateId: {id: e?.secretaryInMandateId, title: e?.lastNameKh + " " + e?.firstNameKh},
          roleInWorkGroup: e?.roleInWorkGroup
        }
      })))
    }
  }, [editData, open]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_work_group")
            : t("modal_title_update_work_group")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers >
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2.5} columnSpacing={1}>

              <Grid item xs={2} >
                <Typography className={t("class-name-field-title")}>
                  {t("thead_group_number")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  className="text-field"
                  placeholder={t("thead_group_number")}
                  {...getFieldProps("numbering")}
                  error={Boolean(touched.numbering && errors.numbering)}
                  helperText={touched.numbering && errors.numbering}
                />
              </Grid>

              <Grid item xs={10}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_group_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_group_name")}
                  {...getFieldProps("work_group_name")}
                  error={Boolean(touched.work_group_name && errors.work_group_name)}
                  helperText={touched.work_group_name && errors.work_group_name}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_responsibilities")}
                </Typography>
                <TextField
                  className="text-field"
                  multiline
                  rows={2}
                  size="small"
                  fullWidth
                  placeholder={t("thead_responsibilities")}
                  {...getFieldProps("responsibilities")}
                  error={Boolean(touched.responsibilities && errors.responsibilities)}
                  helperText={touched.responsibilities && errors.responsibilities}
                />
              </Grid>
              
              <Grid item xs={12}>
                  <ProvincailCouncilList 
                    newProvincailCouncil={newProvincailCouncil}
                    provincailCouncilList={provincailCouncilList} 
                    setProvincailCouncilList={setProvincailCouncilList}
                  />
              </Grid>
              <Grid item xs={12}>
                <SecretaryList 
                  newSecretary={newSecretary}
                  secretaryList={secretaryList}
                  setSecretaryList={setSecretaryList}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
